// OutsourceReceived.jsx - With Currency Context, View Tracking, Month Filter, Search, and PDF/Excel Export
import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { LuChevronLeft, LuChevronRight } from 'react-icons/lu';
import { FaEye, FaSync, FaCheck, FaTimes, FaClock, FaPlay, FaList, FaCircle, FaSearch, FaFilePdf, FaFileExcel } from 'react-icons/fa';
import Footer from '../../../footer.js';
import { Button, Spinner, Modal } from 'react-bootstrap';
import Header from '../../../header/header.js';
import Navvbar from '../../../header/Navvbar.js';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useCurrency } from '../../../../context/currencyContext.js';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import './OutsourceReceived.css';

const OutsourceReceived = () => {
  const { currency, formatAmount } = useCurrency();

  const [outsourceCases, setOutsourceCases] = useState([]);
  const [filteredCases, setFilteredCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLabId, setUserLabId] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatus, setUpdatingStatus] = useState(null);
  const [activeStatusFilter, setActiveStatusFilter] = useState('all');
  const itemsPerPage = 10;

  const [selectedMonth, setSelectedMonth] = useState(new Date());
  const [isMonthFilterActive, setIsMonthFilterActive] = useState(true);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');

  const [showProceduresModal, setShowProceduresModal] = useState(false);
  const [selectedCaseProcedures, setSelectedCaseProcedures] = useState(null);
  const [viewInfoMap, setViewInfoMap] = useState({});

  const [currentLabInfo, setCurrentLabInfo] = useState({
    labName: '',
    address: '',
    phone: '',
    email: '',
    labId: ''
  });

  const [statusCounts, setStatusCounts] = useState({
    pending: 0,
    inProgress: 0,
    completed: 0,
    totalPendingAmount: 0,
    totalInProgressAmount: 0,
    totalCompletedAmount: 0,
    totalAllAmount: 0
  });

  const API_BASE_URL = 'https://hms.backend.hiragroupcloud.com';

  // Refs for race conditions & duplicate fetches
  const abortControllerRef = useRef(null);
  const isMountedRef = useRef(true);

  // ============ CLEANUP ============
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, []);

  // ============ DEBOUNCE SEARCH ============
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // ============ USER INFO (once) ============
  useEffect(() => {
    const loadUserInfo = () => {
      let storedLabId = localStorage.getItem('userLabId');
      if (!storedLabId) storedLabId = sessionStorage.getItem('userLabId');

      const userDataStr = localStorage.getItem('userData') || sessionStorage.getItem('userData');
      let userData = null;
      if (userDataStr) {
        try {
          userData = JSON.parse(userDataStr);
          if (userData.labId && !storedLabId) storedLabId = userData.labId;
        } catch (e) {
          console.error('Error parsing userData:', e);
        }
      }

      if (storedLabId) {
        const formattedLabId = String(storedLabId).padStart(8, '0');
        setUserLabId(formattedLabId);
      } else {
        setLoading(false);
      }

      // ✅ Load current lab info for PDF/Excel header
      setCurrentLabInfo({
        labName: userData?.labName || userData?.name || localStorage.getItem('labName') || 'Your Laboratory',
        address: userData?.address || userData?.labAddress || localStorage.getItem('labAddress') || '',
        phone: userData?.phone || userData?.labPhone || localStorage.getItem('labPhone') || '',
        email: userData?.email || userData?.labEmail || localStorage.getItem('labEmail') || '',
        labId: storedLabId || ''
      });
    };
    loadUserInfo();
  }, []);

  // ============ AUTH HEADER HELPER ============
  const getUserInfo = useCallback(() => {
    const token =
      localStorage.getItem('accessToken') ||
      localStorage.getItem('token') ||
      sessionStorage.getItem('accessToken') ||
      sessionStorage.getItem('token') ||
      '';

    return { token };
  }, []);

  const getAuthHeaders = useCallback(() => {
    const { token } = getUserInfo();
    const headers = {
      'Content-Type': 'application/json'
    };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }
    if (userLabId) {
      headers['userLabId'] = userLabId;
      headers['x-user-lab-id'] = userLabId;
    }
    return headers;
  }, [getUserInfo, userLabId]);

  // ============ HELPERS ============
  const getProceduresArray = useCallback((caseItem) => {
    if (!caseItem) return [];
    if (caseItem.selectedProcedures && Array.isArray(caseItem.selectedProcedures) && caseItem.selectedProcedures.length > 0) {
      return caseItem.selectedProcedures;
    }
    if (caseItem.selectedProcedure && Object.keys(caseItem.selectedProcedure).length > 0) {
      return [caseItem.selectedProcedure];
    }
    return [];
  }, []);

  const calculateCaseTotal = useCallback((caseItem) => {
    const procedures = getProceduresArray(caseItem);
    return procedures.reduce((sum, proc) => {
      if (proc.totalPrice !== undefined && proc.totalPrice !== null) {
        return sum + (parseFloat(proc.totalPrice) || 0);
      }
      const price = proc.price || 0;
      const quantity = proc.quantity || 1;
      return sum + (parseFloat(price) * parseFloat(quantity) || 0);
    }, 0);
  }, [getProceduresArray]);

  const isCaseInSelectedMonth = useCallback((caseItem) => {
    if (!caseItem.createdAt) return false;
    const caseDate = new Date(caseItem.createdAt);
    return caseDate.getMonth() === selectedMonth.getMonth() &&
           caseDate.getFullYear() === selectedMonth.getFullYear();
  }, [selectedMonth]);

  // ✅ Resolve the freshest status for a case.
  const resolveAuthoritativeStatus = useCallback((caseItem, currentViewInfoMap = viewInfoMap) => {
    const viewInfo = currentViewInfoMap?.[caseItem.caseNumber];
    if (viewInfo?.status) return viewInfo.status;
    return caseItem.status;
  }, [viewInfoMap]);

  // ✅ FIX: single source of truth for "is this case cancelled?"
  // Uses the freshest status (bulk view-stats) and falls back to the list status.
  const isCaseCancelled = useCallback((caseItem) => {
    return resolveAuthoritativeStatus(caseItem) === 'cancelled';
  }, [resolveAuthoritativeStatus]);

  const calculateStatusCounts = useCallback((cases, currentViewInfoMap = {}, applyMonthFilter = false) => {
    const counts = {
      pending: 0,
      inProgress: 0,
      completed: 0,
      totalPendingAmount: 0,
      totalInProgressAmount: 0,
      totalCompletedAmount: 0,
      totalAllAmount: 0
    };

    let casesToProcess = cases;
    if (applyMonthFilter) {
      casesToProcess = cases.filter(caseItem => isCaseInSelectedMonth(caseItem));
    }

    casesToProcess.forEach(caseItem => {
      const viewInfo = currentViewInfoMap?.[caseItem.caseNumber];
      const authoritativeStatus = viewInfo?.status || caseItem.status;

      if (authoritativeStatus === 'cancelled') return;

      const caseTotal = calculateCaseTotal(caseItem);
      counts.totalAllAmount += caseTotal;

      if (authoritativeStatus === 'completed') {
        counts.completed++;
        counts.totalCompletedAmount += caseTotal;
        return;
      }

      if (authoritativeStatus === 'accepted' || authoritativeStatus === 'in_progress') {
        counts.inProgress++;
        counts.totalInProgressAmount += caseTotal;
        return;
      }

      if (authoritativeStatus === 'pending') {
        if (!viewInfo?.viewedByDestination) {
          counts.pending++;
          counts.totalPendingAmount += caseTotal;
        } else {
          counts.inProgress++;
          counts.totalInProgressAmount += caseTotal;
        }
      }
    });

    return counts;
  }, [calculateCaseTotal, isCaseInSelectedMonth]);

  // ============ FETCH VIEW INFO (BULK) ============
  const fetchViewInfoForCasesBatch = useCallback(async (cases, signal) => {
    try {
      const { token } = getUserInfo();
      const caseNumbers = cases.map(c => c.caseNumber).filter(Boolean);
      if (caseNumbers.length === 0) return;

      const params = new URLSearchParams();
      params.append('caseNumbers', caseNumbers.join(','));
      params.append('userLabId', userLabId);

      if (isMonthFilterActive) {
        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        params.append('startDate', startOfMonth.toISOString());
        params.append('endDate', endOfMonth.toISOString());
      }

      const response = await axios.get(
        `${API_BASE_URL}/api/outsource-case/view-stats-bulk`,
        {
          params,
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'userLabId': userLabId,
            'x-user-lab-id': userLabId
          },
          withCredentials: true,
          signal
        }
      );

      if (!isMountedRef.current) return;

      if (response.data?.success) {
        const statsMap = response.data.data || {};
        const newViewMap = {};
        Object.keys(statsMap).forEach(caseNumber => {
          const stats = statsMap[caseNumber];
          newViewMap[caseNumber] = {
            viewedByDestination: !!stats.destinationLabView,
            firstViewedAt: stats.firstViewedByDestination,
            isDestinationLab: stats.isDestinationLab || false,
            isSourceLab: stats.isSourceLab || false,
            status: stats.status || null,
            hasInvoice: !!stats.hasInvoice,
            invoiceNumber: stats.invoiceNumber || null
          };
        });

        setViewInfoMap(prev => ({ ...prev, ...newViewMap }));

        setOutsourceCases(prev =>
          prev.map(c => {
            const fresh = newViewMap[c.caseNumber];
            if (!fresh) return c;
            const updates = {};
            if (fresh.status && fresh.status !== c.status) {
              updates.status = fresh.status;
            }
            if (fresh.hasInvoice && !c.invoiceNumber && fresh.invoiceNumber) {
              updates.invoiceNumber = fresh.invoiceNumber;
            }
            return Object.keys(updates).length ? { ...c, ...updates } : c;
          })
        );
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching bulk view info for cases:', error);
    }
  }, [userLabId, getUserInfo, isMonthFilterActive, selectedMonth]);

  // ============ FETCH RECEIVED CASES (with month filter pushed to backend) ============
  const fetchReceivedOutsourceCases = useCallback(async (applyMonthFilter = false) => {
    if (!userLabId) return;

    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      let url = `${API_BASE_URL}/api/outsource-case/lab/${userLabId}/received`;

      const params = new URLSearchParams();
      if (applyMonthFilter) {
        const startOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth(), 1);
        const endOfMonth = new Date(selectedMonth.getFullYear(), selectedMonth.getMonth() + 1, 0, 23, 59, 59, 999);
        params.append('startDate', startOfMonth.toISOString());
        params.append('endDate', endOfMonth.toISOString());
      }

      const queryString = params.toString();
      if (queryString) url += `?${queryString}`;

      const response = await axios.get(url, {
        headers: getAuthHeaders(),
        withCredentials: true,
        signal: controller.signal
      });

      if (!isMountedRef.current) return;

      if (response.data && response.data.success) {
        // ✅ FIX: defensive client-side filter — never keep cancelled cases
        const cases = (response.data.data || []).filter(
          caseItem => caseItem.status !== 'cancelled'
        );

        setViewInfoMap({});
        setOutsourceCases(cases);

        const initialCounts = calculateStatusCounts(cases, {}, false);
        setStatusCounts(initialCounts);

        if (cases.length > 0) {
          fetchViewInfoForCasesBatch(cases, controller.signal);
        }
      } else {
        setError('Failed to fetch received outsource cases');
      }
    } catch (error) {
      if (error.name === 'CanceledError' || error.name === 'AbortError') return;
      console.error('Error fetching received outsource cases:', error);
      if (isMountedRef.current) {
        if (error.response?.status === 401) {
          setError('Session expired. Please log in again.');
          toast.error('Session expired. Please log in again.');
        } else {
          setError(error.response?.data?.message || 'Failed to fetch received outsource cases');
          toast.error('Failed to load received outsource cases');
        }
      }
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  }, [userLabId, selectedMonth, calculateStatusCounts, fetchViewInfoForCasesBatch, getAuthHeaders]);

  // ============ FETCH ON MOUNT + WHENEVER A FILTER CHANGES (single effect) ============
  useEffect(() => {
    if (!userLabId) return;
    fetchReceivedOutsourceCases(isMonthFilterActive);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedMonth, isMonthFilterActive, userLabId]);

  // ============ RECALCULATE COUNTS WHEN viewInfoMap CHANGES ============
  useEffect(() => {
    if (outsourceCases.length > 0 && Object.keys(viewInfoMap).length > 0) {
      const updatedCounts = calculateStatusCounts(outsourceCases, viewInfoMap, false);
      setStatusCounts(updatedCounts);
    }
  }, [viewInfoMap, outsourceCases, calculateStatusCounts]);

  // ============ STATUS CHECKERS ============
  const isCaseTrulyPending = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;
    if (status !== 'pending') return false;
    return !viewInfo?.viewedByDestination;
  }, [viewInfoMap]);

  const isCaseEffectivelyInProgress = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;
    if (status === 'in_progress' || status === 'accepted') return true;
    if (status === 'pending') {
      return viewInfo?.viewedByDestination === true;
    }
    return false;
  }, [viewInfoMap]);

  const isCaseUnread = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const status = viewInfo?.status || caseItem.status;

    if (status === 'completed' || status === 'cancelled') {
      return false;
    }
    if (!viewInfo) return false;
    return !viewInfo.viewedByDestination;
  }, [viewInfoMap]);

  const getEffectiveStatus = useCallback((caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const authoritativeStatus = viewInfo?.status || caseItem.status;

    if (authoritativeStatus === 'pending') {
      if (viewInfo?.viewedByDestination === true) {
        return 'in_progress';
      }
    }
    return authoritativeStatus;
  }, [viewInfoMap]);

  // ============ FILTERED RESULTS ============
  const filteredResults = useMemo(() => {
    if (!outsourceCases.length) return [];

    // ✅ FIX: always drop cancelled cases (using the freshest status) and
    // de-duplicate by _id so the table, the counts and the exports all agree.
    const seen = new Set();
    let filtered = outsourceCases.filter(caseItem => {
      if (isCaseCancelled(caseItem)) return false;
      const key = String(caseItem._id || caseItem.caseNumber);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });

    if (debouncedSearch) {
      const term = debouncedSearch.toLowerCase();
      filtered = filtered.filter(caseItem => {
        const caseNumberMatch = (caseItem.caseNumber || '').toLowerCase().includes(term);
        if (caseNumberMatch) return true;

        const procedures = getProceduresArray(caseItem);
        return procedures.some(proc =>
          (proc.procedureName || '').toLowerCase().includes(term)
        );
      });
    }

    if (activeStatusFilter !== 'all') {
      if (activeStatusFilter === 'in_progress') {
        filtered = filtered.filter(c => isCaseEffectivelyInProgress(c));
      } else if (activeStatusFilter === 'pending') {
        filtered = filtered.filter(c => isCaseTrulyPending(c));
      } else {
        filtered = filtered.filter(caseItem => {
          const viewInfo = viewInfoMap[caseItem.caseNumber];
          const status = viewInfo?.status || caseItem.status;
          return status === activeStatusFilter;
        });
      }
    }

    return filtered;
  }, [
    outsourceCases,
    viewInfoMap,
    activeStatusFilter,
    debouncedSearch,
    isCaseTrulyPending,
    isCaseEffectivelyInProgress,
    isCaseCancelled,
    getProceduresArray,
    isMonthFilterActive
  ]);

  useEffect(() => {
    setFilteredCases(filteredResults);
    setCurrentPage(1);
  }, [filteredResults]);

  // ============ HANDLERS ============
  const goToPreviousMonth = () => {
    setSelectedMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() - 1);
      return newDate;
    });
    setIsMonthFilterActive(true);
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const goToNextMonth = () => {
    setSelectedMonth(prev => {
      const newDate = new Date(prev);
      newDate.setMonth(prev.getMonth() + 1);
      return newDate;
    });
    setIsMonthFilterActive(true);
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const resetMonthFilter = () => {
    setIsMonthFilterActive(false);
    setSelectedMonth(new Date());
    setActiveStatusFilter('all');
    setSearchTerm('');
    setOutsourceCases([]);
    setViewInfoMap({});
    setLoading(true);
  };

  const handleRefresh = () => {
    setOutsourceCases([]);
    setViewInfoMap({});
    fetchReceivedOutsourceCases(isMonthFilterActive);
  };

  const handleStatusFilter = (status) => {
    setActiveStatusFilter(prev => prev === status ? 'all' : status);
  };

  const clearSearch = () => setSearchTerm('');

  // ============ UPDATE STATUS (optimistic, no full refetch) ============
  const updateCaseStatus = async (caseNumber, newStatus) => {
    setUpdatingStatus(caseNumber);
    try {
      const response = await axios.put(
        `${API_BASE_URL}/api/outsource-case/${caseNumber}/status`,
        {
          status: newStatus,
          userLabId: userLabId
        },
        {
          headers: getAuthHeaders(),
          withCredentials: true
        }
      );

      if (response.data && response.data.success) {
        toast.success(`Case status updated to ${newStatus.toUpperCase()}`);

        setOutsourceCases(prev =>
          prev.map(c =>
            c.caseNumber === caseNumber
              ? { ...c, status: newStatus }
              : c
          )
        );

        setViewInfoMap(prev => {
          const existing = prev[caseNumber];
          if (!existing) return prev;
          return {
            ...prev,
            [caseNumber]: { ...existing, status: newStatus }
          };
        });
      } else {
        toast.error('Failed to update case status');
      }
    } catch (error) {
      console.error('Error updating case status:', error);
      if (error.response?.status === 401) {
        toast.error('Session expired. Please log in again.');
      } else {
        toast.error(error.response?.data?.message || 'Failed to update case status');
      }
    } finally {
      setUpdatingStatus(null);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateNumeric = (dateString) => {
    if (!dateString) return '';
    const d = new Date(dateString);
    const dd = String(d.getDate()).padStart(2, '0');
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const yy = String(d.getFullYear()).slice(-2);
    return `${dd}-${mm}-${yy}`;
  };

  const formatTimeAgo = (dateString) => {
    if (!dateString) return null;

    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;

    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getCurrencySymbol = useCallback(() => {
    return currency?.currencySymbol || '₮';
  }, [currency]);

  const formatCurrency = useCallback((amount) => {
    if (amount === null || amount === undefined) return '0.00';
    const numericAmount = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(numericAmount)) return '0.00';

    if (formatAmount) {
      return formatAmount(numericAmount);
    }
    return `${getCurrencySymbol()}${numericAmount.toFixed(2)}`;
  }, [formatAmount, getCurrencySymbol]);

  const formatNumberPlain = (amount) => {
    if (amount === null || amount === undefined) return '0';
    const n = typeof amount === 'string' ? parseFloat(amount) : amount;
    if (isNaN(n)) return '0';
    return n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
  };

  const handleViewOutsource = (caseItem) => {
    window.location.href = `/outsource-case/${caseItem.caseNumber}`;
  };

  const handleViewInvoice = (invoiceNumber, e) => {
    e.preventDefault();
    if (invoiceNumber) {
      window.location.href = `/outsource-invoice/${invoiceNumber}`;
    }
  };

  const handleShowProcedures = (caseItem) => {
    setSelectedCaseProcedures(caseItem);
    setShowProceduresModal(true);
  };

  const handleCloseProceduresModal = () => {
    setShowProceduresModal(false);
    setSelectedCaseProcedures(null);
  };

  const getStatusBadgeStyle = (status) => {
    switch (status) {
      case 'pending':
        return { bg: '#fff3cd', color: '#856404', icon: <FaClock style={{ marginRight: '5px' }} /> };
      case 'accepted':
        return { bg: '#cfe2ff', color: '#084298', icon: <FaCheck style={{ marginRight: '5px' }} /> };
      case 'in_progress':
        return { bg: '#cfe2ff', color: '#0d6efd', icon: <FaPlay style={{ marginRight: '5px' }} /> };
      case 'completed':
        return { bg: '#d1e7dd', color: '#0f5132', icon: <FaCheck style={{ marginRight: '5px' }} /> };
      case 'cancelled':
        return { bg: '#f8d7da', color: '#721c24', icon: <FaTimes style={{ marginRight: '5px' }} /> };
      default:
        return { bg: '#e9ecef', color: '#6c757d', icon: null };
    }
  };

  const renderViewStatus = (caseItem) => {
    const viewInfo = viewInfoMap[caseItem.caseNumber];
    const unread = isCaseUnread(caseItem);

    if (!viewInfo) {
      return <span className="view-status-loading">Loading...</span>;
    }

    if (unread) {
      return (
        <div className="view-status-new">
          <FaCircle className="dot-icon" />
          <span className="new-badge">New</span>
        </div>
      );
    } else {
      const timeAgo = formatTimeAgo(viewInfo.firstViewedAt);
      return (
        <div className="view-status-viewed">
          <FaEye className="eye-icon" />
          <span>{timeAgo ? `Viewed ${timeAgo}` : 'Viewed'}</span>
        </div>
      );
    }
  };

  const formatMonthYear = (date) => {
    return date.toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric'
    });
  };

  // ================================================================
  // EXPORT HELPERS — PDF (invoice-style) & Excel
  // ================================================================

  // ✅ FIX: the exact list of cases that goes into the PDF/Excel.
  // Same list as the table (filteredCases), with a final guard that removes
  // any cancelled case and any duplicate, so export count === screen count.
  const getExportableCases = useCallback(() => {
    const seen = new Set();
    return filteredCases.filter(caseItem => {
      if (isCaseCancelled(caseItem)) return false;
      const key = String(caseItem._id || caseItem.caseNumber);
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [filteredCases, isCaseCancelled]);

  // ✅ FIX: "Sr No" now counts CASES, not procedure lines.
  // A case with 2+ procedures still prints one line per procedure, but only
  // the first line carries the Sr No — so the last Sr No equals the number of
  // cases shown on screen (previously each procedure got its own Sr No, which
  // made the export look like it had more cases than the screen).
  const buildLineItems = (cases) => {
    const lines = [];

    cases.forEach((caseItem, caseIdx) => {
      const procedures = getProceduresArray(caseItem);
      const caseSrNo = caseIdx + 1;

      if (procedures.length > 0) {
        procedures.forEach((proc, procIdx) => {
          const price = parseFloat(proc.price) || 0;
          const qty = parseFloat(proc.quantity) || 1;
          const lineTotal = parseFloat(proc.totalPrice) || (price * qty);

          lines.push({
            srNo: procIdx === 0 ? caseSrNo : '',
            caseNumber: procIdx === 0 ? (caseItem.caseNumber || '') : '',
            description: proc.procedureName || '',
            unit: qty,
            unitPrice: price,
            total: lineTotal,
            sourceLab: caseItem.sourceLab?.labName || '',
            destinationLab: caseItem.destinationLab?.labName || '',
            createdAt: caseItem.createdAt,
            requiredDate: caseItem.requiredDate,
          });
        });
      } else {
        lines.push({
          srNo: caseSrNo,
          caseNumber: caseItem.caseNumber || '',
          description: 'No procedure',
          unit: 0,
          unitPrice: 0,
          total: 0,
          sourceLab: caseItem.sourceLab?.labName || '',
          destinationLab: caseItem.destinationLab?.labName || '',
          createdAt: caseItem.createdAt,
          requiredDate: caseItem.requiredDate,
        });
      }
    });

    return lines;
  };

  // ============ PDF EXPORT (invoice-style) ============
  const exportToPDF = () => {
    const exportCases = getExportableCases();

    if (exportCases.length === 0) {
      toast.info('No cases to export.');
      return;
    }

    const lines = buildLineItems(exportCases);
    const doc = new jsPDF({ orientation: 'portrait', unit: 'pt', format: 'a4' });

    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const marginX = 40;
    const headerTop = 40;

    doc.setFontSize(13);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor('#000000');
    doc.text(currentLabInfo.labName || 'Laboratory', marginX, headerTop);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    let y = headerTop + 16;

    if (currentLabInfo.address) {
      const addressLines = doc.splitTextToSize(currentLabInfo.address, 240);
      doc.text(addressLines, marginX, y);
      y += addressLines.length * 12;
    }
    if (currentLabInfo.phone) {
      doc.text(currentLabInfo.phone, marginX, y);
      y += 12;
    }
    if (currentLabInfo.email) {
      doc.setTextColor('#1a56db');
      doc.text(currentLabInfo.email, marginX, y);
      doc.setTextColor('#000000');
      y += 12;
    }

    const rightX = pageWidth - marginX;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('DATE', rightX, headerTop, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const todayForHeader = formatDateNumeric(new Date());
    doc.text(todayForHeader, rightX, headerTop + 14, { align: 'right' });

    doc.setFont('helvetica', 'bold');
    doc.text('Bill of Month', rightX, headerTop + 32, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    const billOfMonth = isMonthFilterActive
      ? selectedMonth.toLocaleDateString('en-US', { month: 'long' })
      : 'All Time';
    doc.text(billOfMonth, rightX, headerTop + 46, { align: 'right' });

    let billToY = headerTop + 70;
    doc.setFontSize(9);
    doc.setTextColor('#666666');
    doc.text('BILL FROM', marginX, billToY);
    doc.setTextColor('#000000');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);

    const uniqueSourceLabs = [...new Set(exportCases.map(c => c.sourceLab?.labName).filter(Boolean))];
    const sourceLabel = uniqueSourceLabs.length === 1
      ? uniqueSourceLabs[0]
      : uniqueSourceLabs.length > 1
        ? `${uniqueSourceLabs.length} Source Labs`
        : 'N/A';
    doc.text(sourceLabel, marginX, billToY + 16);

    const tableStartY = billToY + 40;

    const tableBody = lines.map((line) => [
      line.srNo,
      line.caseNumber,
      line.description,
      line.unit,
      formatNumberPlain(line.unitPrice),
      formatNumberPlain(line.total),
    ]);

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);

    autoTable(doc, {
      startY: tableStartY,
      head: [['Sr No', 'Case No', 'Description', 'Unit', 'UNIT PRICE', 'TOTAL']],
      body: tableBody,
      theme: 'grid',
      styles: {
        fontSize: 9,
        cellPadding: 5,
        textColor: '#000000',
        lineColor: '#000000',
        lineWidth: 0.3,
      },
      headStyles: {
        fillColor: [0, 74, 173],
        textColor: [255, 255, 255],
        fontStyle: 'bold',
        halign: 'center',
        fontSize: 9,
      },
      columnStyles: {
        0: { halign: 'center', cellWidth: 40 },
        1: { halign: 'center', cellWidth: 55 },
        2: { halign: 'left', cellWidth: 'auto' },
        3: { halign: 'center', cellWidth: 40 },
        4: { halign: 'right', cellWidth: 65 },
        5: { halign: 'right', cellWidth: 65 },
      },
      alternateRowStyles: { fillColor: [250, 250, 250] },
      margin: { left: marginX, right: marginX },
      didDrawPage: () => {},
    });

    let finalY = doc.lastAutoTable.finalY + 10;
    const totalRowX = pageWidth - marginX;

    doc.setFont('helvetica', 'bold');
    doc.setFontSize(10);
    doc.text('GRAND TOTAL', totalRowX - 90, finalY, { align: 'right' });
    doc.text(formatNumberPlain(grandTotal), totalRowX, finalY, { align: 'right' });

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor('#888888');
    doc.text(
      `Generated on ${new Date().toLocaleString()}  |  Total cases: ${exportCases.length}  |  Total line items: ${lines.length}`,
      marginX,
      pageHeight - 20
    );

    const periodSlug = isMonthFilterActive
      ? `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`
      : 'All_Time';
    doc.save(`Outsource_Received_${periodSlug}.pdf`);
  };

  // ============ EXCEL EXPORT ============
  const exportToExcel = () => {
    const exportCases = getExportableCases();

    if (exportCases.length === 0) {
      toast.info('No cases to export.');
      return;
    }

    const lines = buildLineItems(exportCases);

    const todayForHeader = formatDateNumeric(new Date());
    const billOfMonth = isMonthFilterActive
      ? selectedMonth.toLocaleDateString('en-US', { month: 'long' })
      : 'All Time';
    const uniqueSourceLabs = [...new Set(exportCases.map(c => c.sourceLab?.labName).filter(Boolean))];
    const sourceLabel = uniqueSourceLabs.length === 1
      ? uniqueSourceLabs[0]
      : uniqueSourceLabs.length > 1
        ? `${uniqueSourceLabs.length} Source Labs`
        : 'N/A';

    const headerRows = [
      [currentLabInfo.labName || 'Laboratory'],
      [currentLabInfo.address || ''],
      [currentLabInfo.phone || ''],
      [currentLabInfo.email || ''],
      [],
      ['DATE', todayForHeader, '', 'Bill of Month', billOfMonth],
      ['BILL FROM', sourceLabel],
      [],
      ['Sr No', 'Case No', 'Description', 'Unit', 'UNIT PRICE', 'TOTAL'],
    ];

    const bodyRows = lines.map((line) => [
      line.srNo,
      line.caseNumber,
      line.description,
      line.unit,
      line.unitPrice,
      line.total,
    ]);

    const grandTotal = lines.reduce((sum, l) => sum + (parseFloat(l.total) || 0), 0);
    const footerRows = [
      [],
      ['', '', 'TOTAL CASES', exportCases.length, 'GRAND TOTAL', grandTotal],
    ];

    const worksheet = XLSX.utils.aoa_to_sheet([...headerRows, ...bodyRows, ...footerRows]);

    worksheet['!cols'] = [
      { wch: 8 },
      { wch: 14 },
      { wch: 40 },
      { wch: 10 },
      { wch: 14 },
      { wch: 14 },
    ];

    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Outsource Received');

    const periodSlug = isMonthFilterActive
      ? `${selectedMonth.getFullYear()}-${String(selectedMonth.getMonth() + 1).padStart(2, '0')}`
      : 'All_Time';
    XLSX.writeFile(workbook, `Outsource_Received_${periodSlug}.xlsx`);
  };

  const paginatedCases = useMemo(() => {
    const indexOfLastItem = currentPage * itemsPerPage;
    const indexOfFirstItem = indexOfLastItem - itemsPerPage;
    return filteredCases.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredCases, currentPage]);

  const totalPages = Math.ceil(filteredCases.length / itemsPerPage);

  const statusCards = useMemo(() => [
    {
      key: 'pending',
      label: 'Pending Cases',
      count: statusCounts.pending,
      amount: statusCounts.totalPendingAmount,
      icon: <FaClock />,
      accent: '#d97706',
      tint: '#fffaf0',
      activeTint: '#fef3e2'
    },
    {
      key: 'in_progress',
      label: 'In Progress',
      count: statusCounts.inProgress,
      amount: statusCounts.totalInProgressAmount,
      icon: <FaPlay />,
      accent: '#2563eb',
      tint: '#f5f8ff',
      activeTint: '#e8f0fe'
    },
    {
      key: 'completed',
      label: 'Completed',
      count: statusCounts.completed,
      amount: statusCounts.totalCompletedAmount,
      icon: <FaCheck />,
      accent: '#0f9d58',
      tint: '#f4fbf6',
      activeTint: '#e6f6ea'
    }
  ], [statusCounts]);

  const getCardStyleVars = (card, isActive) => ({
    '--card-accent': card.accent,
    '--card-tint': card.tint,
    '--card-active-tint': card.activeTint,
    '--card-shadow': `${card.accent}26`,
    '--card-hover-shadow': `${card.accent}22`,
    '--card-accent-33': `${card.accent}33`,
    '--card-accent-55': `${card.accent}55`
  });

  // ============ TABLE ROWS (memoized) ============
  const tableRows = useMemo(() => {
    return paginatedCases.map((caseItem) => {
      const procedures = getProceduresArray(caseItem);
      const procedureCount = procedures.length;
      const unread = isCaseUnread(caseItem);
      const effectiveStatus = getEffectiveStatus(caseItem);
      const displayStatusStyle = getStatusBadgeStyle(effectiveStatus);

      const getStatusDisplayText = () => {
        const viewInfo = viewInfoMap[caseItem.caseNumber];
        const authoritativeStatus = viewInfo?.status || caseItem.status;
        if (authoritativeStatus === 'pending' && isCaseEffectivelyInProgress(caseItem)) {
          return 'IN PROGRESS';
        }
        return (authoritativeStatus || 'PENDING').toUpperCase().replace('_', ' ');
      };

      return (
        <tr key={caseItem._id} className={unread ? 'unread-row' : ''}>
          <td className="td-case-number">
            {caseItem.caseNumber}
            {unread && <span className="unread-dot" />}
          </td>
          <td className="td-created">{formatDate(caseItem.createdAt)}</td>
          <td className="td-source-lab">{caseItem.sourceLab?.labName || 'N/A'}</td>
          <td className="td-procedures">
            {procedureCount > 1 ? (
              <button className="procedures-btn" onClick={() => handleShowProcedures(caseItem)}>
                <FaList /> {procedureCount} Procedures
              </button>
            ) : (
              <span className="procedure-text">
                {procedures[0]?.procedureName || 'N/A'}
              </span>
            )}
          </td>
          <td className="td-required-date">{formatDate(caseItem.requiredDate)}</td>
          <td>
            <span
              className="status-text"
              style={{ color: displayStatusStyle.color }}
            >
              {displayStatusStyle.icon}
              {getStatusDisplayText()}
            </span>
          </td>
          <td className="view-status-cell">{renderViewStatus(caseItem)}</td>
          <td>
            {caseItem.invoiceNumber ? (
              <a
                href={`/outsource-invoice/${caseItem.invoiceNumber}`}
                className="invoice-link"
                onClick={(e) => handleViewInvoice(caseItem.invoiceNumber, e)}
              >
                {caseItem.invoiceNumber}
              </a>
            ) : (
              <span className="no-invoice-text">No invoice</span>
            )}
          </td>
          <td className="action-cell">
            <div className="action-buttons">
              <Button
                variant={unread ? 'primary' : 'outline-primary'}
                size="sm"
                className="view-case-btn"
                onClick={() => handleViewOutsource(caseItem)}
              >
                <FaEye style={{ marginRight: '3px' }} />
                View Case
              </Button>

              {caseItem.status === 'accepted' && (
                <Button
                  variant="primary"
                  size="sm"
                  onClick={() => updateCaseStatus(caseItem.caseNumber, 'in_progress')}
                  disabled={updatingStatus === caseItem.caseNumber}
                >
                  Start Work
                </Button>
              )}
            </div>
          </td>
        </tr>
      );
    });
  }, [
    paginatedCases,
    viewInfoMap,
    updatingStatus,
    getProceduresArray,
    isCaseUnread,
    getEffectiveStatus,
    isCaseEffectivelyInProgress
  ]);

  return (
    <>
      <Header />
      <Navvbar />

      <div className="outsource-received-container">

        {/* Header */}
        <div className="outsource-received-header">
          <div className="header-title-section">
            <h2 className="page-title">
              Outsource Cases Received
              {isMonthFilterActive && (
                <span className="month-label">({formatMonthYear(selectedMonth)})</span>
              )}
              <span className="total-amount">({formatCurrency(statusCounts.totalAllAmount)})</span>
            </h2>
            {activeStatusFilter !== 'all' && (
              <p className="filter-indicator">
                Showing {activeStatusFilter === 'in_progress' ? 'in progress (including viewed pending)' : activeStatusFilter.replace('_', ' ')} cases
                <button
                  className="clear-status-filter-btn"
                  onClick={() => setActiveStatusFilter('all')}
                >
                  Clear Status Filter
                </button>
              </p>
            )}
          </div>

          <div className="header-controls">
            <div className="month-navigation">
              <button className="month-nav-btn" onClick={goToPreviousMonth} title="Previous Month">
                <LuChevronLeft size={20} />
              </button>
              <span className="month-display">{formatMonthYear(selectedMonth)}</span>
              <button className="month-nav-btn" onClick={goToNextMonth} title="Next Month">
                <LuChevronRight size={20} />
              </button>
            </div>

            <div className="header-buttons-group">
              {isMonthFilterActive && (
                <button className="reset-filter-btn" onClick={resetMonthFilter}>
                  Reset Filter
                </button>
              )}

              <button className="export-btn export-pdf-btn" onClick={exportToPDF} title="Download PDF">
                <FaFilePdf /> PDF
              </button>
              <button className="export-btn export-excel-btn" onClick={exportToExcel} title="Download Excel">
                <FaFileExcel /> Excel
              </button>

              <button className="refresh-btn" onClick={handleRefresh}>
                <FaSync /> Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Search Bar */}
        <div className="search-bar-wrapper">
          <div className="search-bar-inner">
            <FaSearch className="search-icon" />
            <input
              type="text"
              placeholder="Search by Case Number or Procedure Name..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="search-input"
            />
            {searchTerm && (
              <button className="clear-search-btn" onClick={clearSearch} title="Clear search">
                <FaTimes />
              </button>
            )}
          </div>
          {debouncedSearch && (
            <p className="search-info">
              Searching for: <strong>"{debouncedSearch}"</strong>
              {filteredCases.length > 0 && ` — ${filteredCases.length} result${filteredCases.length !== 1 ? 's' : ''} found`}
            </p>
          )}
        </div>

        {/* Status Summary Cards */}
        <div className="status-cards-grid">
          {statusCards.map((card) => {
            const isActive = activeStatusFilter === card.key;
            return (
              <div
                key={card.key}
                onClick={() => handleStatusFilter(card.key)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    handleStatusFilter(card.key);
                  }
                }}
                className={`status-card ${isActive ? 'active' : ''}`}
                style={getCardStyleVars(card, isActive)}
              >
                <div className="status-card-accent-bar" />

                <div className="status-icon">{card.icon}</div>

                <div className="status-content">
                  <div className="status-numbers">
                    <span className="status-count">{card.count}</span>
                    <span className="status-amount">{formatCurrency(card.amount)}</span>
                  </div>
                  <span className="status-label">
                    {card.label}
                    {isMonthFilterActive && (
                      <span className="status-filtered-tag">(filtered)</span>
                    )}
                  </span>
                </div>

                {isActive && (
                  <div className="active-badge">
                    <FaCheck size={9} /> Filtered
                  </div>
                )}
              </div>
            );
          })}
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <Spinner animation="border" className="loading-spinner" />
            <p className="loading-text">
              {isMonthFilterActive
                ? `Loading received cases for ${formatMonthYear(selectedMonth)}...`
                : 'Loading all received cases...'}
            </p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="error-container">
            <p className="error-text">Error: {error}</p>
            <Button variant="outline-danger" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        )}

        {/* Table */}
        {!loading && !error && (
          <>
            <div className="table-responsive">
              <table className="outsource-table">
                <thead>
                  <tr>
                    <th>Case #</th>
                    <th>Created</th>
                    <th>Source Lab</th>
                    <th>Procedures</th>
                    <th>Required Date</th>
                    <th>Status</th>
                    <th>View Status</th>
                    <th>Invoice</th>
                    <th className="th-center">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.length > 0 ? (
                    tableRows
                  ) : (
                    <tr>
                      <td colSpan={9} className="empty-state-cell">
                        {debouncedSearch ? (
                          <>
                            No cases found matching "<strong>{debouncedSearch}</strong>"
                            {isMonthFilterActive && ` in ${formatMonthYear(selectedMonth)}`}
                          </>
                        ) : (isMonthFilterActive || activeStatusFilter !== 'all') ? (
                          <>
                            No outsource cases found
                            {isMonthFilterActive && ` for ${formatMonthYear(selectedMonth)}`}
                            {activeStatusFilter !== 'all' && ` with status: ${activeStatusFilter.replace('_', ' ')}`}
                          </>
                        ) : (
                          'No outsource cases received yet'
                        )}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="pagination-container">
                <Button variant="outline-secondary" onClick={() => setCurrentPage(1)} disabled={currentPage === 1} size="sm">
                  First
                </Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} disabled={currentPage === 1} size="sm">
                  Previous
                </Button>
                <Button variant="primary" disabled size="sm" className="pagination-page-btn">
                  Page {currentPage} of {totalPages}
                </Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} disabled={currentPage === totalPages} size="sm">
                  Next
                </Button>
                <Button variant="outline-secondary" onClick={() => setCurrentPage(totalPages)} disabled={currentPage === totalPages} size="sm">
                  Last
                </Button>
              </div>
            )}

            <div className="table-footer-info">
              Showing {filteredCases.length} case{filteredCases.length !== 1 ? 's' : ''}
              {isMonthFilterActive && ` for ${formatMonthYear(selectedMonth)}`}
              {activeStatusFilter !== 'all' && ` with status: ${activeStatusFilter.replace('_', ' ')}`}
              {debouncedSearch && ` matching "${debouncedSearch}"`}
            </div>
          </>
        )}

        {/* Procedures Modal */}
        <Modal show={showProceduresModal} onHide={handleCloseProceduresModal} size="lg" centered>
          <Modal.Header closeButton className="modal-header-custom">
            <Modal.Title className="modal-title-custom">
              {selectedCaseProcedures && (
                <>Procedures for Case #{selectedCaseProcedures.caseNumber}</>
              )}
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            {selectedCaseProcedures && (
              <>
                <div className="modal-case-info">
                  <p>
                    <strong>Source Lab:</strong> {selectedCaseProcedures.sourceLab?.labName || 'N/A'}
                  </p>
                  <p>
                    <strong>Required Date:</strong> {formatDate(selectedCaseProcedures.requiredDate)}
                  </p>
                  <p>
                    <strong>Status:</strong> {
                      (() => {
                        const viewInfo = viewInfoMap[selectedCaseProcedures.caseNumber];
                        const authoritativeStatus = viewInfo?.status || selectedCaseProcedures.status;
                        if (authoritativeStatus === 'pending' && isCaseEffectivelyInProgress(selectedCaseProcedures)) {
                          return 'IN PROGRESS';
                        }
                        return (authoritativeStatus || 'PENDING').toUpperCase().replace('_', ' ');
                      })()
                    }
                  </p>
                </div>

                <div className="modal-table-wrapper">
                  <table className="modal-procedures-table">
                    <thead>
                      <tr>
                        <th className="th-left">#</th>
                        <th className="th-left">Procedure Name</th>
                        <th className="th-right">Price</th>
                        <th className="th-center">Quantity</th>
                        <th className="th-right">Total Price</th>
                        <th className="th-left">Shared By</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getProceduresArray(selectedCaseProcedures).map((procedure, index) => (
                        <tr key={procedure._id || index}>
                          <td>{index + 1}</td>
                          <td>{procedure.procedureName || 'N/A'}</td>
                          <td className="td-right">{formatCurrency(procedure.price)}</td>
                          <td className="td-center">{procedure.quantity || 1}</td>
                          <td className="td-right td-bold">
                            {formatCurrency(procedure.totalPrice || (procedure.price * (procedure.quantity || 1)))}
                          </td>
                          <td className="td-shared-by">{procedure.sharedByName || 'N/A'}</td>
                        </tr>
                      ))}
                    </tbody>
                    <tfoot>
                      <tr>
                        <td colSpan={4} className="td-grand-total-label">Grand Total:</td>
                        <td className="td-grand-total-value">
                          {formatCurrency(calculateCaseTotal(selectedCaseProcedures))}
                        </td>
                        <td></td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </>
            )}
          </Modal.Body>
          <Modal.Footer className="modal-footer-custom">
            <Button variant="secondary" onClick={handleCloseProceduresModal} size="sm">
              Close
            </Button>
            {selectedCaseProcedures && (
              <Button
                variant="primary"
                size="sm"
                className="modal-view-details-btn"
                onClick={() => {
                  handleCloseProceduresModal();
                  handleViewOutsource(selectedCaseProcedures);
                }}
              >
                View Full Details
              </Button>
            )}
          </Modal.Footer>
        </Modal>
      </div>

      <Footer />
    </>
  );
};

export default OutsourceReceived;